import { useState } from 'react';
import UtilityBar from '../components/layout/UtilityBar';
import Nav from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import LincolnAvatar from '../components/ui/LincolnAvatar';
import Waveform from '../components/ui/Waveform';
import AuthModal from '../components/common/AuthModal';
import { Link } from 'react-router-dom';

const STEPS = [
  {
    n: '01', title: 'Upload your material',
    desc: "Drop in PDFs, videos, slides, podcasts, web URLs, or recorded lectures. Lincoln ingests them all. We've also imported entire LMS exports from Canvas, Moodle, and Schoology.",
    bullets: ['PDF, DOCX, PPTX, MP4, MP3, ePub', 'Up to 500 GB per course', 'Imports from Canvas, Moodle, Schoology', 'Auto-summarization at upload'],
    mockup: 'upload', color: 'var(--yellow)',
  },
  {
    n: '02', title: 'Lincoln becomes a tutor for it',
    desc: 'Within minutes, Lincoln has built a domain-expert voice tutor on your content — quoting your material, citing your sources, in your voice.',
    bullets: ['Cites your material in answers', 'Stays grounded — never hallucinates outside your content', 'Tone-matched to your brand', 'Available in 3 languages instantly'],
    mockup: 'brain', color: 'var(--green)',
  },
  {
    n: '03', title: 'Auto-generate everything',
    desc: 'Quizzes, flashcards, worksheets, podcasts, even short-form videos — all generated from your material, personalized to each learner.',
    bullets: ['Adaptive quizzes after every lesson', 'Spaced-repetition flashcards', 'AI-narrated podcast summaries', 'Per-learner progress dashboards'],
    mockup: 'gen', color: 'var(--blue)',
  },
  {
    n: '04', title: 'Certify learners with QR badges',
    desc: 'Issue your branded certificates — co-signed by Lincoln — with verifiable QR codes anchored on a public registry. Optional blockchain anchoring for high-stakes credentials.',
    bullets: ['Your logo, your colors, your wording', 'QR-verifiable on a public registry', 'Optional blockchain anchoring', "One-click 'Add to LinkedIn'"],
    mockup: 'cert', color: 'var(--navy)',
  },
];

const PRICING = [
  { name: 'Individual',       price: 'Free',           desc: 'For solo tutors and educators.',
    features: ['1 course', 'Up to 200 learners', 'Lincoln branding', 'Basic analytics', 'Community support'],
    cta: 'Start free', featured: false },
  { name: 'Institution',      price: 'From €1,200/mo', desc: 'For schools, universities, and tutoring agencies.',
    features: ['Unlimited courses', 'Up to 10,000 learners', 'Your branding on certs', 'Advanced analytics + LMS sync', 'Dedicated success manager', 'SSO, SAML, FERPA'],
    cta: 'Talk to sales', featured: true },
  { name: 'National Program', price: 'Custom',         desc: 'For ministries, NGOs, and large rollouts.',
    features: ['Everything in Institution', 'Unlimited learners', 'Custom voice + curriculum', 'On-prem deployment option', 'Multilingual co-design', 'Local-language onboarding'],
    cta: 'Book a call', featured: false },
];

const FAQ = [
  ['How long does setup take?', 'Most providers go from sign-up to live course in under 4 hours. Lincoln learns your material in minutes; the rest is branding and review.'],
  ['Does Lincoln hallucinate?', 'Lincoln is grounded in your uploaded material — he cites your sources, and refuses to answer outside-context questions unless you allow it. You set the rules.'],
  ['What languages can Lincoln teach in?', '3 languages out of the box, including Finnish, Swedish, Maltese, Arabic, Urdu, Hindi, Mandarin, Spanish, French, German, and Swahili. Need another? We can add it in 1–2 weeks.'],
  ['Can I white-label?', 'Yes — Institution and National plans include full white-labeling of the learner experience, certificates, and dashboard.'],
  ['What about data privacy and FERPA?', 'GDPR compliant. ISO 27001 certified. SOC 2 Type II. FERPA-aligned. Optional on-prem deployment for high-sensitivity contexts.'],
  ['What if my learners don\'t have great internet?', 'Lincoln has an offline-first mobile app that syncs progress when reconnected. Voice mode runs at 64 kbps — works on 3G.'],
];

export default function ProviderOnboarding() {
  const [authOpen, setAuthOpen] = useState(false);
  return (
    <div style={{ background: 'var(--bg)' }}>
      <UtilityBar />
      <Nav variant="light" />

      {/* HERO */}
      <section className="relative overflow-hidden" style={{ background: 'linear-gradient(160deg, var(--navy) 0%, var(--navy-3) 100%)', padding: '100px 0 80px' }}>
        <div className="absolute pointer-events-none rounded-full"
          style={{ top: -200, right: -100, width: 700, height: 700, background: 'radial-gradient(circle, rgba(34,181,114,0.18), transparent 65%)' }} />
        <div className="lc-container on-navy relative" style={{ zIndex: 1 }}>
          <div className="grid items-center" style={{ gridTemplateColumns: '1.2fr 1fr', gap: 60 }}>
            <div>
              <div className="inline-flex items-center gap-2 rounded-full font-bold tracking-widest mb-6"
                style={{ padding: '8px 14px', background: 'rgba(34,181,114,0.18)', border: '1px solid rgba(34,181,114,0.4)', fontSize: 12, color: '#7be3b0' }}>
                FOR EDUCATION PROVIDERS
              </div>
              <h1 className="text-white" style={{ fontSize: 76, letterSpacing: '-0.03em', lineHeight: 1 }}>
                Your course.<br />
                <span style={{ color: 'var(--yellow)' }}>Lincoln&apos;s voice.</span><br />
                Global reach.
              </h1>
              <p className="mt-6 leading-relaxed" style={{ fontSize: 19, color: 'rgba(255,255,255,0.78)', maxWidth: 540 }}>
                Bring your teaching material. Within an hour, Lincoln becomes a voice tutor on your content — quizzing, certifying, translating to 3 languages, scaling to any country.
              </p>
              <div className="flex gap-3.5 mt-9">
                <button type="button" onClick={() => setAuthOpen(true)} className="lc-btn primary lg">Become a provider</button>
              </div>

              <div className="flex items-center mt-10 pt-7" style={{ gap: 28, borderTop: '1px solid rgba(255,255,255,0.08)' }}>
                {[
                  { v: '5+',  l: 'Active providers' },
                  { v: '15', l: 'Courses live' },
                  { v: '10',   l: 'Countries' },
                  { v: '55k', l: 'Learners reached' },
                ].map((s) => (
                  <div key={s.l}>
                    <div className="font-serif font-semibold" style={{ fontSize: 32, color: 'var(--yellow)' }}>{s.v}</div>
                    <div className="mt-0.5" style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)' }}>{s.l}</div>
                  </div>
                ))}
              </div>
            </div>

            <UploadAnimation />
          </div>
        </div>
      </section>

      {/* Trust strip */}
      <section style={{ padding: '60px 0', background: 'var(--bg-cream)', borderBottom: '1px solid var(--line)' }}>
        <div className="lc-container">
          <div className="text-center font-bold tracking-widest mb-6" style={{ fontSize: 11, color: 'var(--muted)' }}>JOIN PROVIDERS LIKE</div>
          <div className="flex justify-between items-center flex-wrap" style={{ gap: 36 }}>
            {['World Economic Forum', 'Republic of Malta', 'Finland · OPH', 'City of Fontana', 'Glowie', 'Aalto University', 'Helsinki University'].map((t) => (
              <span key={t} className="font-serif font-medium opacity-70" style={{ fontSize: 18, color: 'var(--ink-2)' }}>{t}</span>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="lc-section">
        <div className="lc-container">
          <div className="text-center" style={{ marginBottom: 56 }}>
            <span className="lc-eyebrow">How it works</span>
            <h2 className="mt-3.5">From your course to a Lincoln tutor in one afternoon.</h2>
          </div>

          {STEPS.map((s, i) => (
            <div key={s.n} className="grid items-center" style={{ gridTemplateColumns: '1fr 1fr', gap: 64, marginBottom: 80 }}>
              <div style={{ order: i % 2 === 0 ? 0 : 1 }}>
                <div className="inline-flex items-center justify-center font-serif font-bold mb-5"
                  style={{
                    width: 72, height: 72, borderRadius: 18,
                    background: s.color,
                    color: i === 3 ? 'var(--yellow)' : 'var(--navy)',
                    fontSize: 32,
                    boxShadow: `0 12px 30px ${s.color}44`,
                  }}>{s.n}</div>
                <h2 style={{ fontSize: 44 }}>{s.title}</h2>
                <p className="mt-4 mb-6 leading-relaxed" style={{ fontSize: 17, color: 'var(--muted)', maxWidth: 480 }}>{s.desc}</p>
                <ul className="list-none p-0 m-0 flex flex-col gap-2.5">
                  {s.bullets.map((b) => (
                    <li key={b} className="flex items-center gap-2.5" style={{ fontSize: 14, color: 'var(--ink-2)' }}>
                      <span className="inline-flex items-center justify-center rounded-full"
                        style={{ width: 20, height: 20, background: s.color }}>
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={i === 3 ? 'var(--yellow)' : 'var(--navy)'} strokeWidth="3.5"><polyline points="20,6 9,17 4,12" /></svg>
                      </span>
                      {b}
                    </li>
                  ))}
                </ul>
              </div>
              <div style={{ order: i % 2 === 0 ? 1 : 0 }}>
                <StepMockup kind={s.mockup} />
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* PRICING */}
      <section className="lc-section" style={{ background: 'var(--bg-cream)' }}>
        <div className="lc-container">
          <div className="text-center mb-12">
            <span className="lc-eyebrow">Pricing</span>
            <h2 className="mt-3.5">Three ways to deploy Lincoln.</h2>
            <p className="mx-auto mt-3.5" style={{ color: 'var(--muted)', fontSize: 17, maxWidth: 580 }}>
              Free for individual tutors and educators. Custom for institutions. Always voice-first.
            </p>
          </div>
          <div className="grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)', gap: 20 }}>
            {PRICING.map((t) => (
              <div key={t.name} className="relative"
                style={{
                  background: t.featured ? 'var(--navy)' : '#fff',
                  color: t.featured ? '#fff' : 'var(--ink)',
                  borderRadius: 20, padding: 32,
                  border: t.featured ? '2px solid var(--yellow)' : '1px solid var(--line)',
                }}>
                {t.featured && (
                  <span className="absolute font-bold tracking-wider"
                    style={{ top: -12, left: 24, background: 'var(--yellow)', color: 'var(--navy)', padding: '5px 12px', borderRadius: 999, fontSize: 11 }}>MOST POPULAR</span>
                )}
                <div className="font-bold tracking-widest mb-2" style={{ fontSize: 12, color: t.featured ? 'var(--yellow-2)' : 'var(--muted)' }}>{t.name.toUpperCase()}</div>
                <div className="font-serif font-semibold mb-1" style={{ fontSize: 40, color: t.featured ? '#fff' : 'var(--ink)', letterSpacing: '-0.01em' }}>{t.price}</div>
                <div style={{ fontSize: 14, color: t.featured ? 'rgba(255,255,255,0.7)' : 'var(--muted)' }}>{t.desc}</div>
                <ul className="list-none p-0 flex flex-col gap-2.5" style={{ margin: '24px 0' }}>
                  {t.features.map((f) => (
                    <li key={f} className="flex items-start gap-2.5" style={{ fontSize: 14, color: t.featured ? 'rgba(255,255,255,0.85)' : 'var(--ink-2)' }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={t.featured ? 'var(--yellow)' : 'var(--green-2)'} strokeWidth="3" style={{ marginTop: 3, flexShrink: 0 }}><polyline points="20,6 9,17 4,12" /></svg>
                      {f}
                    </li>
                  ))}
                </ul>
                <a href="#" className={`lc-btn ${t.featured ? 'yellow' : 'primary'} lg w-full`}>{t.cta}</a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="lc-section">
        <div className="lc-container" style={{ maxWidth: 840 }}>
          <div className="text-center mb-10">
            <span className="lc-eyebrow">FAQ</span>
            <h2 className="mt-3.5">Questions providers ask.</h2>
          </div>
          {FAQ.map(([q, a], i) => (
            <details key={q} style={{ borderBottom: '1px solid var(--line)', padding: '20px 0' }} open={i === 0}>
              <summary className="cursor-pointer flex justify-between items-center font-semibold"
                style={{ listStyle: 'none', fontSize: 18 }}>
                {q}
                <span style={{ fontSize: 24, color: 'var(--muted)' }}>+</span>
              </summary>
              <p className="mt-3 leading-relaxed" style={{ color: 'var(--muted)', fontSize: 15 }}>{a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="on-navy" style={{ background: 'var(--navy)', padding: '100px 0' }}>
        <div className="lc-container text-center">
          <span className="lc-eyebrow on-dark">Ready to launch?</span>
          <h2 className="mx-auto mt-3.5 text-white" style={{ fontSize: 64, maxWidth: 720 }}>
            Your learners deserve a tutor that actually talks to them.
          </h2>
          <p className="mx-auto mt-4" style={{ fontSize: 18, color: 'rgba(255,255,255,0.7)', maxWidth: 580 }}>
            Let&apos;s design what works for your context, whether you&apos;re a tutor, a school, or a ministry.
          </p>
          <div className="inline-flex gap-3.5 mt-9">
            <button type="button" onClick={() => setAuthOpen(true)} className="lc-btn primary lg">Start free</button>
          </div>
        </div>
      </section>

      <Footer />

      <AuthModal
        isOpen={authOpen}
        onClose={() => setAuthOpen(false)}
        initialMode="signup"
        initialRole="provider"
      />
    </div>
  );
}

function UploadAnimation() {
  return (
    <div className="text-white"
      style={{ background: 'rgba(255,255,255,0.05)', borderRadius: 24, padding: 28, border: '1px solid rgba(255,255,255,0.1)', backdropFilter: 'blur(20px)' }}>
      <div className="font-bold tracking-widest mb-3" style={{ fontSize: 11, color: 'var(--yellow-2)' }}>WHAT YOUR PROVIDER COCKPIT FEELS LIKE</div>

      <div className="text-center mb-4"
        style={{ background: 'rgba(240,184,12,0.08)', border: '2px dashed rgba(240,184,12,0.4)', borderRadius: 16, padding: 24 }}>
        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="var(--yellow)" strokeWidth="2" style={{ marginBottom: 8 }}>
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
          <polyline points="17 8 12 3 7 8" />
          <line x1="12" y1="3" x2="12" y2="15" />
        </svg>
        <div className="text-white font-semibold" style={{ fontSize: 14 }}>Drop your course materials here</div>
        <div className="mt-1" style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)' }}>PDF · MP4 · PPTX · MP3 · URL · LMS export</div>
      </div>

      <div className="flex flex-col gap-2">
        {[
          { name: 'Module 1 — Intro to Machine Learning.pdf', size: '2.4 MB',  status: '✓ Indexed',            color: 'var(--green)' },
          { name: 'Lecture 3 — Bias in AI.mp4',               size: '412 MB',  status: '✓ Transcribed',        color: 'var(--green)' },
          { name: 'Worksheet 4.docx',                          size: '84 KB',   status: '⟳ Generating quiz…',   color: 'var(--yellow)' },
          { name: 'Reading list.url',                          size: '—',       status: '⟳ Crawling…',           color: 'var(--yellow)' },
        ].map((f) => (
          <div key={f.name} className="flex items-center gap-2.5"
            style={{ background: 'rgba(255,255,255,0.06)', borderRadius: 10, padding: '10px 14px', fontSize: 12 }}>
            <span className="flex-1">
              <div className="text-white font-semibold">{f.name}</div>
              <div className="mt-0.5" style={{ fontSize: 10, color: 'rgba(255,255,255,0.5)' }}>{f.size}</div>
            </span>
            <span className="font-semibold" style={{ color: f.color, fontSize: 11 }}>{f.status}</span>
          </div>
        ))}
      </div>

      <div className="mt-4" style={{ padding: '12px 14px', background: 'rgba(34,181,114,0.12)', borderRadius: 10, fontSize: 12 }}>
        <div className="flex justify-between mb-2">
          <span className="font-semibold" style={{ color: '#7be3b0' }}>Lincoln is learning your course…</span>
          <span className="font-bold" style={{ color: '#7be3b0' }}>74%</span>
        </div>
        <div className="overflow-hidden" style={{ height: 6, background: 'rgba(255,255,255,0.1)', borderRadius: 3 }}>
          <div style={{ width: '74%', height: '100%', background: 'var(--green)' }} />
        </div>
        <div className="mt-2" style={{ color: 'rgba(255,255,255,0.6)', fontSize: 11 }}>Est. ready in 8 minutes</div>
      </div>
    </div>
  );
}

function StepMockup({ kind }) {
  if (kind === 'upload') return <UploadAnimation />;

  if (kind === 'brain') return (
    <div style={{ background: 'var(--bg-cream)', borderRadius: 20, padding: 28, border: '1px solid var(--line)' }}>
      <div className="font-bold tracking-widest mb-3.5" style={{ fontSize: 11, color: 'var(--muted)' }}>LINCOLN, READING YOUR CONTENT</div>
      <div className="grid items-center gap-3.5 mb-4" style={{ gridTemplateColumns: '70px 1fr' }}>
        <LincolnAvatar size={70} pulse />
        <div>
          <div className="italic mb-1.5" style={{ fontSize: 14, color: 'var(--muted)' }}>&ldquo;Reading Module 3, Chapter 4 — Bias in Training Data…&rdquo;</div>
          <Waveform bars={20} color="var(--green)" />
        </div>
      </div>
      <div className="flex flex-col gap-2">
        {[
          ['Vocabulary extracted', '847 terms'],
          ['Concepts mapped',      '62 nodes'],
          ['Quiz questions generated', '184'],
          ['Languages prepared',   '40'],
        ].map(([k, v]) => (
          <div key={k} className="flex justify-between bg-white" style={{ padding: '10px 14px', borderRadius: 8, fontSize: 13, border: '1px solid var(--line)' }}>
            <span style={{ color: 'var(--ink-2)' }}>{k}</span>
            <span className="font-bold" style={{ color: 'var(--green-2)' }}>{v}</span>
          </div>
        ))}
      </div>
    </div>
  );

  if (kind === 'gen') return (
    <div className="bg-white" style={{ borderRadius: 20, padding: 28, border: '1px solid var(--line)' }}>
      <div className="font-bold tracking-widest mb-3.5" style={{ fontSize: 11, color: 'var(--muted)' }}>AUTO-GENERATED FOR YOU</div>
      <div className="grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        {[
          { icon: '📝', t: 'Quizzes',      n: '184' },
          { icon: '🃏', t: 'Flashcards',   n: '412' },
          { icon: '🎙', t: 'Podcast eps.', n: '24' },
          { icon: '📄', t: 'Worksheets',   n: '62' },
          { icon: '🏅', t: 'Cert. tracks', n: '3' },
          { icon: '🌐', t: 'Languages',    n: '3' },
        ].map((g) => (
          <div key={g.t} style={{ background: 'var(--bg-cream)', borderRadius: 10, padding: 14 }}>
            <div style={{ fontSize: 24 }}>{g.icon}</div>
            <div className="font-serif font-bold mt-1.5" style={{ fontSize: 24, color: 'var(--ink)' }}>{g.n}</div>
            <div style={{ fontSize: 11, color: 'var(--muted)' }}>{g.t}</div>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="text-center" style={{ background: 'var(--navy)', borderRadius: 20, padding: 28 }}>
      <div className="text-white font-serif" style={{ fontSize: 20 }}>Certificate mockup</div>
      <p className="text-white opacity-70 mt-4" style={{ fontSize: 13 }}>
        See the live <Link to="/certificates/ai-101" className="underline">Certificate page →</Link>
      </p>
    </div>
  );
}
